import React, { useState, useEffect } from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { useTaskContext } from "../context/TaskContext";
import Avatar from "../components/Avatar";
import { useAuth } from "../context/AuthContext";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";

const pastelColors = [
  "#FBE6FC", // Rosa
  "#D9F4F8", // Azul claro
  "#FFECE1", // Naranja suave
  "#E1F6FF", // Azul distinto
  "#6ae88e", // Verde claro
];

const tagTextColors = [
  "#FF00B8", // Texto Rosa
  "#268FB0", // Texto Azul claro
  "#FF5C00", // Texto Naranja
  "#2C62B4", // Texto Azul distinto
  "#038554", // Texto Verde
];

const TaskBoard = ({
  selectedProject,
  projectLists,
  projectTasks,
  listColors,
  zoomLevel,
  openNewListModal,
  handleDeleteListClick,
  openTaskModal,
  openDetailModal,
  openAttachModal,
}) => {
  const { updateTask, deleteTask } = useTaskContext();
  const [isDragging, setIsDragging] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [visibleLists, setVisibleLists] = useState({});
  const { user: authUser } = useAuth();
  const { formatDate } = useFunctionsPrevia();

  useEffect(() => {
    const newVisibleLists = projectLists.reduce((acc, lst) => {
      const tasksInList = projectTasks.filter((t) => t.status === lst.name);
      acc[lst.name] = tasksInList.length > 0; // o true si quieres siempre abierto
      return acc;
    }, {});
    setVisibleLists(newVisibleLists);
  }, [projectLists, projectTasks]);

  const getContainerWidth = () => "300px";

  const getColumnCount = () => {
    switch (zoomLevel) {
      case 1:
        return 4;
      case 2:
        return 3;
      case 3:
        return 2;
      case 4:
        return 1;
      default:
        return 4;
    }
  };

  const toggleListVisibility = (listName) => {
    setVisibleLists((prev) => ({
      ...prev,
      [listName]: !prev[listName],
    }));
  };

  const onDragStart = () => setIsDragging(true);
  const onDragEnd = async (result) => {
    setIsDragging(false);
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }
    if (source.droppableId !== destination.droppableId) {
      await updateTask(draggableId, {
        status: destination.droppableId,
      });
    }
  };

  const toggleMenu = (id) => {
    setActiveMenu((prev) => (prev === id ? null : id));
  };

  return (
    <section className="kanban__main">
      <div
        className="kanban__main-wrapper"
        style={{
          gridTemplateColumns: `repeat(${getColumnCount()}, 1fr)`,
        }}
      >
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          {projectLists.map((lst) => {
            const status = lst.name;
            const tasksInList = projectTasks.filter((t) => t.status === status);
            const statusCapitalized =
              status.charAt(0).toUpperCase() +
              status.slice(1).replace(/-/g, " ");
            return (
              <Droppable key={status} droppableId={status}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`${status.replace(
                      / /g,
                      "-"
                    )}-color card-wrapper ${isDragging ? "dragging" : ""}`}
                    style={{
                      flexBasis: getContainerWidth(),
                      maxWidth: getContainerWidth(),
                      borderTop: `4px solid ${listColors[status] || "#007bff"}`,
                      backgroundColor: "#f6f8fc",
                      height: "auto",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      margin: "0 10px",
                    }}
                  >
                    <div className="card-wrapper__header">
                      <div
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: listColors[status] || "#007bff",
                          flexShrink: 0,
                        }}
                      ></div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                      >
                        <span className="backlog-name">
                          {statusCapitalized}
                        </span>
                        <span
                          style={{
                            backgroundColor: "#e0e0e0",
                            borderRadius: "50%",
                            height: "20px",
                            width: "20px",
                            fontSize: "12px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontWeight: "500",
                            color: "#333",
                          }}
                        >
                          {tasksInList.length}
                        </span>
                      </div>
                      <div className="backlog-dots">
                        <div className="menu-container">
                          <i
                            className="bi bi-three-dots"
                            onClick={() => toggleMenu(status)}
                            style={{ cursor: "pointer" }}
                          ></i>
                          {activeMenu === status && (
                            <div className="menu-list">
                              <button
                                onClick={() =>
                                  openNewListModal({
                                    name: status,
                                    color: listColors[status],
                                  })
                                }
                                className="menu-button"
                              >
                                <i
                                  className="bi bi-pencil"
                                  style={{ marginRight: "5px" }}
                                ></i>
                                Editar
                              </button>
                              <button
                                className="menu-button deleteMenu-button"
                                onClick={() => handleDeleteListClick(status)}
                              >
                                <i
                                  className="bi bi-trash"
                                  style={{ marginRight: "5px" }}
                                ></i>
                                Eliminar
                              </button>
                            </div>
                          )}
                        </div>
                        <i
                          className={`bi ${
                            visibleLists[status]
                              ? "bi-chevron-down"
                              : "bi-chevron-up"
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => toggleListVisibility(status)}
                        ></i>
                        <div
                          className="add-task-ico"
                          onClick={() => openTaskModal(status)}
                          style={{
                            backgroundColor: `${
                              listColors[status] || "#007bff"
                            }33`,
                            color: listColors[status] || "#007bff",
                          }}
                        >
                          <i className="bi bi-plus"></i>
                        </div>
                      </div>
                    </div>
                    {visibleLists[status] ? (
                      tasksInList.length === 0 ? (
                        <p>No hay tareas disponibles</p>
                      ) : (
                        tasksInList.map((task, idx) => (
                          <Draggable
                            key={task.id}
                            draggableId={task.id}
                            index={idx}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="card"
                                style={{
                                  ...provided.draggableProps.style,
                                  backgroundColor: snapshot.isDragging
                                    ? "#f4f4f4"
                                    : "#fff",
                                  padding: "8px",
                                  marginBottom: "8px",
                                }}
                              >
                                <div className="card__header">
                                  <div className="card__header-tags">
                                    {task.tags && task.tags.length > 0 ? (
                                      task.tags.map((tag, i) => (
                                        <div
                                          key={i}
                                          style={{
                                            backgroundColor:
                                              pastelColors[
                                                i % pastelColors.length
                                              ],
                                            color:
                                              tagTextColors[
                                                i % tagTextColors.length
                                              ],
                                            borderRadius: "4px",
                                            padding: "4px 9px",
                                            fontSize: "13px",
                                            fontWeight: "bold",
                                            maxWidth: "120px",
                                            textAlign: "center",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            marginRight: "5px",
                                          }}
                                        >
                                          {tag}
                                        </div>
                                      ))
                                    ) : (
                                      <span
                                        style={{
                                          color: "#6c757d",
                                          fontStyle: "italic",
                                          fontSize: "12px",
                                        }}
                                      >
                                        Sin etiquetas
                                      </span>
                                    )}
                                  </div>
                                  <div className="card__header-clear">
                                    <i
                                      className="bi bi-three-dots"
                                      onClick={() => toggleMenu(task.id)}
                                    ></i>
                                  </div>
                                  {activeMenu === task.id && (
                                    <div className="menu-list">
                                      <button
                                        onClick={() => {
                                          openTaskModal(task.status, task);
                                          toggleMenu(null);
                                        }}
                                        className="menu-button"
                                      >
                                        <i
                                          className="bi bi-pencil"
                                          style={{ marginRight: "5px" }}
                                        ></i>
                                        Editar
                                      </button>
                                      <button
                                        onClick={() => {
                                          deleteTask(task.id);
                                          toggleMenu(null);
                                        }}
                                        className="menu-button deleteMenu-button"
                                      >
                                        <i
                                          className="bi bi-trash"
                                          style={{ marginRight: "5px" }}
                                        ></i>
                                        Eliminar
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div className="card__text">{task.title}</div>
                                <div
                                  className="card__attachments-comments"
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "5px",
                                    marginTop: "10px",
                                  }}
                                >
                                  <div
                                    onClick={() => openDetailModal(task)}
                                    className="comments-wrapper"
                                  >
                                    <i className="bi bi-chat-left-text comments-ico"></i>
                                    <span className="comments-num">
                                      {task.comments ? task.comments.length : 0}{" "}
                                      Comentarios
                                    </span>
                                  </div>
                                  <div
                                    className="attach-wrapper"
                                    onClick={() => openAttachModal(task)}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "5px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <i
                                      className="bi bi-link-45deg attach-ico"
                                      style={{
                                        color: "#555",
                                        fontSize: "16px",
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    ></i>
                                    <span className="attach-num">
                                      {(task.attachments || []).length} Archivos
                                      Adjuntos
                                    </span>
                                  </div>
                                </div>
                                <div className="card__footer">
                                  <div className="card__footer-user">
                                    <Avatar
                                      userId={task.userId || authUser.uid}
                                      size={30}
                                      className="card-avatar"
                                    />
                                    <span>
                                      {task.userName ||
                                        task.user ||
                                        authUser.displayName}
                                    </span>
                                  </div>
                                  <div className="card__footer-date">
                                    <i className="bi bi-calendar4-event"></i>{" "}
                                    {formatDate(task.date)}
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))
                      )
                    ) : null}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            );
          })}
        </DragDropContext>
      </div>
    </section>
  );
};

export default TaskBoard;
